export interface MapViewportBounds {
    west: number;
    south: number;
    east: number;
    north: number;
}

const BBOX_PRECISION = 6;

export const DEFAULT_MAP_VIEWPORT_PADDING = 0.5;
export const TRUNCATED_MAP_VIEWPORT_PADDING = 0.15;
export const DENSE_CITY_MIN_LOT_ZOOM = 15;

export function shouldLoadIndividualLots(city: string, zoom: number): boolean {
    const denseCity = city === 'copenhagen' || city === 'frederiksberg';
    return !denseCity || zoom >= DENSE_CITY_MIN_LOT_ZOOM;
}

export function isMapViewportResponseTruncated(total: number | undefined, returned: number): boolean {
    return typeof total === 'number' && total > returned;
}

export function parseMapViewportBbox(bbox: string): MapViewportBounds | null {
    const values = bbox.split(',').map(Number);
    if (
        values.length !== 4 ||
        values.some((value) => !Number.isFinite(value)) ||
        values[0] >= values[2] ||
        values[1] >= values[3]
    ) {
        return null;
    }

    return {
        west: values[0],
        south: values[1],
        east: values[2],
        north: values[3],
    };
}

export function formatMapViewportBbox(bounds: MapViewportBounds): string {
    return [bounds.west, bounds.south, bounds.east, bounds.north]
        .map((value) => value.toFixed(BBOX_PRECISION))
        .join(',');
}

export function expandMapViewportBbox(
    bbox: string,
    paddingRatio = DEFAULT_MAP_VIEWPORT_PADDING
): string {
    const bounds = parseMapViewportBbox(bbox);
    if (!bounds || !Number.isFinite(paddingRatio) || paddingRatio < 0) return bbox;

    const longitudePadding = (bounds.east - bounds.west) * paddingRatio;
    const latitudePadding = (bounds.north - bounds.south) * paddingRatio;

    return formatMapViewportBbox({
        west: bounds.west - longitudePadding,
        south: bounds.south - latitudePadding,
        east: bounds.east + longitudePadding,
        north: bounds.north + latitudePadding,
    });
}

export function mapViewportContains(coverageBbox: string, visibleBbox: string): boolean {
    const coverage = parseMapViewportBbox(coverageBbox);
    const visible = parseMapViewportBbox(visibleBbox);
    if (!coverage || !visible) return false;

    return visible.west >= coverage.west &&
        visible.south >= coverage.south &&
        visible.east <= coverage.east &&
        visible.north <= coverage.north;
}

export function shouldRefreshMapViewport(
    coverageBbox: string,
    visibleBbox: string,
    responseTruncated: boolean
): boolean {
    return responseTruncated || !mapViewportContains(coverageBbox, visibleBbox);
}
